import { isNotNil, toGroupedMap } from '@newfront-insurance/core';
import type { UIMessage } from 'ai';
import { differenceInDays, isToday, isYesterday } from 'date-fns';
import pluralize from 'pluralize';

import type { UIMessageFileMetadata, UIMessageMetadata, UIMessageWithMetadata } from '../../../lib/types';
import type { GetChatWithMessagesMessage } from '../../../queries/chats';
import type { GetChatsByFiltersChat } from '../../../queries/chats';

function isMessageFileMetadata(value: unknown): value is UIMessageFileMetadata {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as UIMessageFileMetadata).uuid === 'string' &&
    typeof (value as UIMessageFileMetadata).filename === 'string'
  );
}

function getPersistedMessageFiles(metadata: UIMessageMetadata | null): UIMessageFileMetadata[] {
  return Array.isArray(metadata?.files) ? metadata.files.filter(isMessageFileMetadata) : [];
}

function getBackendMetadata(metadata: unknown): UIMessageMetadata | null {
  return metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? (metadata as UIMessageMetadata) : null;
}

function getMessageFilesWithPersistedMetadata({
  message,
  persistedFiles,
}: {
  message: GetChatWithMessagesMessage;
  persistedFiles: UIMessageFileMetadata[];
}) {
  const persistedFilesByUuid = new Map(persistedFiles.map((file) => [file.uuid, file]));

  if (!message.files || message.files.length === 0) {
    return persistedFiles;
  }

  return message.files
    .map((file) => {
      if (!file.uuid) {
        return null;
      }

      const persistedFile = persistedFilesByUuid.get(file.uuid);
      const filename = file.filename ?? persistedFile?.filename ?? 'Attachment';

      return {
        ...persistedFile,
        uuid: file.uuid,
        filename,
      };
    })
    .filter(isNotNil);
}

/**
 * Converts backend message format to Vercel AI SDK message format.
 * Vercel AI SDK uses ID, backend uses uuid.
 */
export function backendMessageToSDKMessage(message: GetChatWithMessagesMessage): UIMessageWithMetadata {
  const backendMetadata = getBackendMetadata(message.metadata);
  const persistedFiles = getPersistedMessageFiles(backendMetadata);

  return {
    ...message,
    id: message.uuid!,
    role: message.role as UIMessage['role'],
    parts: message.parts ? (message.parts as UIMessageWithMetadata['parts']) : [],
    metadata: {
      ...(backendMetadata ?? {}),
      reaction: message.reaction,
      files: getMessageFilesWithPersistedMetadata({ message, persistedFiles }),
      clarification: backendMetadata?.clarification,
      contextUsage: backendMetadata?.contextUsage,
    },
  };
}

/**
 * Splits chat history results into sections based on their creation date.
 * For today, section title is "Today"
 * For today - 1, section title is "Yesterday"
 * For today - 2, section title is "2 days ago"
 * Up to 7 days ago, section title is "7 days ago"
 * Anything after, section title is "More than a week ago"
 */
export function groupChatsByDate(chats: GetChatsByFiltersChat[]): {
  title: string;
  chats: GetChatsByFiltersChat[];
}[] {
  const today = new Date();
  const formattedChats = chats
    .sort((a, b) => {
      // Order by most recent first
      if (a.lastMessageAt && b.lastMessageAt) {
        return new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime();
      }
      return 0;
    })
    .filter((chat) => isNotNil(chat.lastMessageAt))
    .map((chat) => {
      const date = new Date(chat.lastMessageAt!);
      // If the date is today, return "Today"
      if (isToday(date)) {
        return { groupTitle: 'Today', ...chat };
      }
      // If the date is yesterday, return "Yesterday"
      if (isYesterday(date)) {
        return { groupTitle: 'Yesterday', ...chat };
      }
      // Calculate the number of days ago
      const daysAgo = differenceInDays(today, date);
      // If the date is more than a week ago, return "More than a week ago"
      const groupTitle = daysAgo > 7 ? 'More than a week ago' : `${daysAgo} ${pluralize('day', daysAgo)} ago`;
      return { groupTitle, ...chat };
    });

  const groupedChats = toGroupedMap(formattedChats, (chat) => chat.groupTitle);

  return Array.from(groupedChats.entries()).map(([groupTitle, chatsInGroup]) => ({
    title: groupTitle,
    chats: chatsInGroup,
  }));
}
